import { useState } from "react"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import { errorToast, toast } from "@/v2-ui/toast"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import { useFeatureTypeQuery } from "@/v2-backoffice-client/feature/type/feature.types.query"
import { useFeatureUpdateMutation } from "@/v2-backoffice-client/feature/update/feature.update.mutation"
import type { FeatureFragment } from "@/v2-backoffice-client/feature/__types__/feature.fragment"

export type FeatureTypeSectionProps = {
  className?: string
  feature: FeatureFragment
  /** Surface segment of this section's feature codes, e.g. "Features". */
  featureCodeLabel: string
}

/**
 * What kind of thing a feature is — the column that decides how the rest of
 * the entry is read, including which editor the Configuration tab offers.
 *
 * Retyping is allowed only between kinds no dedicated catalog claims membership
 * of. Base maps, map layers and nav layers are each found BY this column by the
 * catalog that owns them, so changing it moves the entry between catalogs
 * rather than relabelling it, and one side is always left without the other.
 * IsCatalogOwned marks those kinds; the server refuses the write regardless of
 * what this picker offers, so the greying-out here is a courtesy rather than
 * the guard.
 */
function FeatureTypeSection(props: FeatureTypeSectionProps) {
  const { feature, featureCodeLabel, className } = props

  const { data } = useFeatureTypeQuery()
  const featureTypes = data?.getFeatureTypes ?? []

  // The saved row is the baseline; `draft` is the unsaved edit on top of it and
  // is dropped whenever the row underneath changes.
  const [draft, setDraft] = useState<number | null>(null)
  const [savedKey, setSavedKey] = useState<string | null>(null)
  const currentKey = `${feature.Code}:${feature.FeatureTypeCode}`
  if (currentKey !== savedKey) {
    setSavedKey(currentKey)
    setDraft(null)
  }

  const value = draft ?? feature.FeatureTypeCode

  const [save, { loading: isSaving, error }] = useFeatureUpdateMutation({
    onCompleted() {
      setDraft(null)
      toast({ title: "Kind saved to the live runtime." })
    },
  })
  errorToast(error, { title: "Failed to save the kind" })

  const currentType = featureTypes.find((type) => type.Code === value)
  // Locked on the feature's STORED kind, not the drafted one: it is the stored
  // side that owns a gis row, and it is what makes every target illegal.
  const isLocked =
    featureTypes.find((type) => type.Code === feature.FeatureTypeCode)
      ?.IsCatalogOwned ?? false

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <Label htmlFor="featureType">Kind</Label>
          <Select
            value={String(value)}
            disabled={isSaving || isLocked}
            onValueChange={(next) => setDraft(Number(next))}
          >
            <Select.Trigger id="featureType" size="md" className="max-w-md">
              <Select.Value placeholder={feature.FeatureTypeName} />
            </Select.Trigger>
            <Select.Content>
              {featureTypes.map((featureType) => (
                <Select.Item
                  key={featureType.Code}
                  value={String(featureType.Code)}
                  // A catalog-owned kind is never a legal target either: the
                  // feature would claim to be a basemap with no basemap row.
                  disabled={featureType.IsCatalogOwned}
                >
                  {featureType.Name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
          {isLocked ? (
            <p className="text-xs text-muted-foreground">
              The {currentType?.Name ?? "this kind"} catalog finds its entries
              by this column, so changing it would move this entry out of that
              catalog rather than relabel it — and leave the data it keeps
              behind with nothing to belong to. It is decided when the entry is
              created.
            </p>
          ) : (
            currentType?.Description && (
              <p className="text-xs text-muted-foreground">
                {currentType.Description}
              </p>
            )
          )}
        </div>

        <Button
          size="sm"
          className="mt-6"
          isLoading={isSaving}
          disabled={draft === null}
          data-feature-code={`UI_Backoffice${featureCodeLabel}_Type_Save`}
          onClick={() => {
            void save({
              variables: {
                input: { FeatureCode: feature.Code, FeatureTypeCode: value },
              },
            })
          }}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

export default FeatureTypeSection
